import { useCallback, useEffect, useState } from "react";
import { useCounter, useToggle } from "react-use";

import { useTokenId } from "../stores/hooks/useTokenId";
import { FetcherResult, fetcher } from "./fetcher";

export type PostJsonResponse<Data, Payload> = {
  data: Data | null;
  error: Error | null;
  loading: boolean;
  sendJson(payload: Payload): Promise<FetcherResult<Data>>;
  version: number;
};

export const usePostJson = <Data, Payload>(
  url: string
): PostJsonResponse<Data, Payload> => {
  const [loading, setLoading] = useToggle(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<Data | null>(null);
  const tokenId = useTokenId();
  const [version, { inc }] = useCounter(0);

  useEffect(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, [url]);

  return {
    data,
    error,
    loading,
    sendJson: useCallback(
      (payload: Payload) => {
        setLoading(true);
        const requestInit = {
          body: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
        };
        return fetcher<Data>(url, requestInit)
          .then((response) => {
            setData(response.payload);
            setError(null);
            setLoading(false);
            inc();
            return response;
          })
          .catch((err) => {
            setLoading(false);
            setError(err);
            setData(null);
            throw err;
          });
      },

      [url, tokenId]
    ),
    version,
  };
};
